// Wormhole subspace: formation director for the bonus level (spec
// .agent/tasks/wormhole-subspace). Subspace attackers fight in army-tactics
// FORMATIONS with a Galaga-style seeded escalation: each formation
// materializes as a recognizable pattern (a shared telegraph — enemies never
// pop in), holds a formation flight for a beat via the `formation_drive`
// steering override (enemy-ai.ts, same channel boss-ai uses), then releases
// its members into their def's normal pursuit.
//
// Containment: everything here allocates from world.enemies, which the pool
// swap (state.ts enterSubspace) has already re-pointed at the DEDICATED
// subspace pool — this module can never touch a normal-space enemy.
//
// Deterministic: formation picks, layouts, and portal placement come from a
// private RngStream owned by BonusGame (the salt pattern of UpgradeSystem);
// per-tick drives are pure functions of sim state. No Math.random/wall-clock.

import type { WorldState } from './state.js';
import type { Enemy, Vec2 } from '../types.js';
import { wrapArena, wrappedDelta } from './collision.js';
import { findEnemyDef, initEnemy, VOID_FIEND_ID } from './director.js';
import { RngStream } from './rng.js';

/**
 * Subspace kills credit SUBSPACE_SCORE_MULTIPLIER × the normal per-kill
 * score (game.ts SCORE_PER_KILL = 100): the bonus level pays double.
 * SUBSPACE_KILL_SCORE is the derived absolute value the sim banks on
 * world.bonus.subspace_score; game.ts credits the same value to the wallet.
 */
export const SUBSPACE_SCORE_MULTIPLIER = 2;
export const SUBSPACE_KILL_SCORE = 100 * SUBSPACE_SCORE_MULTIPLIER;

/** Formation archetypes fielded in subspace, in seeded-chain draw order. */
export const SUBSPACE_FORMATION_IDS = [
  'ring',
  'phalanx',
  'pincer',
  'spearhead',
  'wedge'
] as const;
export type SubspaceFormationId = (typeof SUBSPACE_FORMATION_IDS)[number];

const DRIFTFIN_ID = 'enemy.driftfin';
const DARTLING_ID = 'enemy.dartling';
const WEAVER_ID = 'enemy.weaver';
const BULWARK_ID = 'enemy.bulwark';

/** A formation is replaced once wiped or once this old (12 s at 60 Hz). */
const FORMATION_TTL_TICKS = 12 * 60;
/** Whole-pattern materialize telegraph (0.75 s at 60 Hz): the formation
 * appears as one shape, never a trickle of pop-ins. */
const FORMATION_TELEGRAPH_TICKS = 45;
/** Escalation: formation N>0 grows counts by this much per index and speeds
 * the drive by this much per index (capped). */
const ESCALATE_COUNT_PER_INDEX = 2;
const ESCALATE_SPEED_PER_INDEX = 0.06;
const ESCALATE_SPEED_CAP = 1.35;

/** Exit portal placement: seeded ring around the player's entry point. */
const EXIT_PORTAL_MIN_DIST = 22;
const EXIT_PORTAL_MAX_DIST = 30;
const EXIT_PORTAL_RADIUS = 2.2;

/** Per-member bookkeeping; the Enemy ref is validated by id every tick
 * (pool-slot reuse flips the id, so stale refs drop out deterministically). */
interface Member {
  e: Enemy;
  id: number;
  /** Role index — semantics per formation (phalanx: peel order; others: 0). */
  role: number;
}

function distToPlayer(world: WorldState, e: Enemy): { d: Vec2; dist: number } {
  const d = wrappedDelta(world.player.pos, e.pos); // direction e → player
  return { d, dist: Math.hypot(d.x, d.y) };
}

/**
 * The subspace formation director. Owned by BonusGame (one per run, reset()
 * after world.reset); all per-trip state lives here and on world.bonus.
 */
export class SubspaceDirector {
  private readonly world: WorldState;
  private readonly stream: RngStream;
  private members: Member[] = [];
  /** Tick the current formation spawned (TTL chain timer). */
  private formationTick = 0;
  /** Last fielded formation, so the chain never repeats back-to-back. */
  private lastId: SubspaceFormationId | null = null;

  constructor(world: WorldState, stream: RngStream) {
    this.world = world;
    this.stream = stream;
  }

  /** Drop per-trip formation state (the pool itself is cleared by state.ts). */
  reset(): void {
    this.members = [];
    this.lastId = null;
  }

  /**
   * Field the next formation: seeded pick (no immediate repeat), layout and
   * size escalated by world.bonus.formation_index. All members materialize
   * together at `telegraphUntil` so the pattern reads before it moves.
   */
  spawnNext(tick: number, telegraphUntil: number, forceId?: SubspaceFormationId): void {
    const b = this.world.bonus;
    let pick: SubspaceFormationId;
    if (forceId) {
      pick = forceId;
    } else {
      pick = SUBSPACE_FORMATION_IDS[this.stream.next_int(SUBSPACE_FORMATION_IDS.length)];
      if (pick === this.lastId) {
        pick = SUBSPACE_FORMATION_IDS[this.stream.next_int(SUBSPACE_FORMATION_IDS.length)];
      }
    }
    this.lastId = pick;
    b.formation_id = pick;
    this.members = [];
    this.formationTick = tick;

    const index = b.formation_index;
    b.formation_index++;
    const grow = ESCALATE_COUNT_PER_INDEX * index;
    const player = this.world.player.pos;
    // Seeded axis the formation is anchored on (direction player → formation).
    const theta = this.stream.next_range(0, Math.PI * 2);
    const axis = { x: Math.cos(theta), y: Math.sin(theta) };

    switch (pick) {
      case 'ring': this.spawnRing(player, tick, telegraphUntil, grow); break;
      case 'phalanx': this.spawnPhalanx(player, axis, tick, telegraphUntil, grow); break;
      case 'pincer': this.spawnPincer(player, axis, tick, telegraphUntil, grow); break;
      case 'spearhead': this.spawnSpearhead(player, axis, tick, telegraphUntil, grow); break;
      case 'wedge': this.spawnWedge(player, axis, tick, telegraphUntil, grow); break;
    }
  }

  /** Place the exit portal on a seeded ring around the player (always
   * reachable, never under the ship) and open it. */
  spawnExitPortal(tick: number): void {
    const portal = this.world.bonus.exit_portal;
    const angle = this.stream.next_range(0, Math.PI * 2);
    const dist = this.stream.next_range(EXIT_PORTAL_MIN_DIST, EXIT_PORTAL_MAX_DIST);
    const pos = {
      x: this.world.player.pos.x + Math.cos(angle) * dist,
      y: this.world.player.pos.y + Math.sin(angle) * dist
    };
    wrapArena(pos);
    portal.active = true;
    portal.pos = pos;
    portal.radius = EXIT_PORTAL_RADIUS;
    portal.collapse_tick = -1;
    this.world.events.emit(
      'spawn',
      { kind: 'wormhole', exit: true, pos: { x: pos.x, y: pos.y }, radius: portal.radius },
      tick
    );
  }

  /**
   * Per-tick formation update (call before updateEnemies): write each live
   * member's formation_drive, release members whose attack run has begun
   * (they revert to their def's pursuit), and chain the next formation once
   * the field is wiped or the current one outlives its TTL.
   */
  update(tick: number): void {
    const b = this.world.bonus;
    const fighting = b.phase === 'fighting';
    const esc = Math.min(ESCALATE_SPEED_CAP, 1 + ESCALATE_SPEED_PER_INDEX * (b.formation_index - 1));

    const kept: Member[] = [];
    for (const m of this.members) {
      if (!m.e.alive || m.e.id !== m.id) continue; // died (or slot reused)
      kept.push(m);
      if (tick < m.e.telegraph_until) continue; // still materializing
      const release = this.driveMember(m, tick, esc);
      if (release) m.e.formation_drive = undefined;
    }
    this.members = kept;

    // Chain: a wiped field or a stale formation fields the next wave. Only
    // while fighting — the opening formation telegraphs through 'entering'.
    if (fighting && (this.members.length === 0 || tick - this.formationTick > FORMATION_TTL_TICKS)) {
      this.spawnNext(tick, tick + FORMATION_TELEGRAPH_TICKS);
    }
  }

  // --- Formation drives: write m.e.formation_drive; return true to release --

  private driveMember(m: Member, tick: number, esc: number): boolean {
    switch (this.lastId) {
      case 'ring': return this.driveRing(m, esc);
      case 'phalanx': return this.drivePhalanx(m, tick, esc);
      case 'pincer': return this.drivePincer(m, esc);
      case 'spearhead': return this.driveSpearhead(m, esc);
      case 'wedge': return this.driveWedge(m, esc);
      // Members only exist after spawnNext set lastId — unreachable.
      case null: return false;
    }
  }

  /** Tightening encirclement: spiral inward with a strong tangential swirl
   * (whirlpool idiom); release into driftfin orbit-hunt once the noose closes. */
  private driveRing(m: Member, esc: number): boolean {
    const { d, dist } = distToPlayer(this.world, m.e);
    if (dist < 8) return true;
    const inv = 1 / Math.max(dist, 1e-6);
    const ux = d.x * inv;
    const uy = d.y * inv;
    // Mostly inward, part tangential → the ring visibly tightens while turning.
    m.e.formation_drive = { x: ux * 0.8 - uy * 0.6, y: uy * 0.8 + ux * 0.6, speed_mult: 1.1 * esc };
    return false;
  }

  /** Phalanx: a slow wall advance; back-row dartlings peel off one at a time
   * (role = peel order) into lunge-intercept dive-bombs. The wall releases
   * when it reaches the ship. */
  private drivePhalanx(m: Member, tick: number, esc: number): boolean {
    const { d, dist } = distToPlayer(this.world, m.e);
    if (m.role > 0) {
      // Peeler: held in the wall until its slot in the peel order comes up.
      const peelTick = this.formationTick + m.role * 40;
      if (tick >= peelTick) return true;
    } else if (dist < 7) {
      return true;
    }
    const inv = 1 / Math.max(dist, 1e-6);
    m.e.formation_drive = { x: d.x * inv, y: d.y * inv, speed_mult: 0.55 * esc };
    return false;
  }

  /** Pincer (double envelopment): two wings arc to flank points beside the
   * ship; on arrival the weavers' flank-hunt completes the encirclement. */
  private drivePincer(m: Member, esc: number): boolean {
    const player = this.world.player.pos;
    const { d, dist } = distToPlayer(this.world, m.e);
    if (dist < 6.5) return true;
    // Flank point: 5 u beside the player, on the wing's own side.
    const inv = 1 / Math.max(dist, 1e-6);
    const side = m.role === 0 ? 1 : -1;
    const flank = { x: player.x + -d.y * inv * 5 * side, y: player.y + d.x * inv * 5 * side };
    wrapArena(flank);
    const to = wrappedDelta(flank, m.e.pos);
    const len = Math.hypot(to.x, to.y);
    if (len < 1) return true;
    m.e.formation_drive = { x: to.x / len, y: to.y / len, speed_mult: 1.15 * esc };
    return false;
  }

  /** Spearhead: a single file charging the ship at one locked speed (the
   * tip's), so the column holds together until it hits. */
  private driveSpearhead(m: Member, esc: number): boolean {
    const { d, dist } = distToPlayer(this.world, m.e);
    if (dist < 8) return true;
    const inv = 1 / Math.max(dist, 1e-6);
    // Lock every file member to the bulwark tip's pace so the column arrives
    // as a spear, not a trail.
    const tipSpeed = findEnemyDef(BULWARK_ID).max_speed * 1.15 * esc;
    m.e.formation_drive = {
      x: d.x * inv,
      y: d.y * inv,
      speed_mult: tipSpeed / findEnemyDef(m.e.def_id).max_speed
    };
    return false;
  }

  /** Wedge: a V driving straight at the ship; releases into pursuit on
   * contact range. */
  private driveWedge(m: Member, esc: number): boolean {
    const { d, dist } = distToPlayer(this.world, m.e);
    if (dist < 8) return true;
    const inv = 1 / Math.max(dist, 1e-6);
    m.e.formation_drive = { x: d.x * inv, y: d.y * inv, speed_mult: 1.0 * esc };
    return false;
  }

  // --- Spawn layouts (all torus-wrapped; pattern doubles as the telegraph) --

  private acquireMember(
    defId: string,
    pos: Vec2,
    tick: number,
    telegraphUntil: number,
    role: number
  ): void {
    const e = this.world.enemies.acquire();
    if (!e) {
      this.world.events.emit('pool_exhausted', { pool: 'subspace' }, tick);
      return;
    }
    wrapArena(pos);
    const def = findEnemyDef(defId);
    initEnemy(e, def, pos, this.world.player.pos, this.world.runTime, tick, telegraphUntil);
    this.members.push({ e, id: e.id, role });
    this.world.events.emit(
      'spawn',
      { kind: 'enemy', def_id: def.id, pos: { x: pos.x, y: pos.y }, telegraph_until: telegraphUntil },
      tick
    );
  }

  /** Ring: driftfins materialize on a full circle around the player. */
  private spawnRing(player: Vec2, tick: number, telegraphUntil: number, grow: number): void {
    const count = Math.min(26, 10 + grow);
    const radius = 24;
    const baseAngle = this.stream.next_range(0, Math.PI * 2);
    for (let i = 0; i < count; i++) {
      const a = baseAngle + (i / count) * Math.PI * 2;
      this.acquireMember(
        DRIFTFIN_ID,
        { x: player.x + Math.cos(a) * radius, y: player.y + Math.sin(a) * radius },
        tick,
        telegraphUntil,
        0
      );
    }
  }

  /** Phalanx: void-fiend rows perpendicular to the approach axis, dartling
   * dive-bombers ranked behind the wall (role = peel order, 1-based). */
  private spawnPhalanx(
    player: Vec2,
    axis: Vec2,
    tick: number,
    telegraphUntil: number,
    grow: number
  ): void {
    const cols = 4 + Math.min(3, grow);
    const rows = 2 + Math.min(2, Math.floor(grow / 2));
    const spacing = 2.4;
    const origin = { x: player.x + axis.x * 30, y: player.y + axis.y * 30 };
    const perp = { x: -axis.y, y: axis.x };
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const along = (r - (rows - 1) / 2) * spacing;
        const across = (c - (cols - 1) / 2) * spacing;
        this.acquireMember(
          VOID_FIEND_ID,
          {
            x: origin.x + axis.x * along + perp.x * across,
            y: origin.y + axis.y * along + perp.y * across
          },
          tick,
          telegraphUntil,
          0
        );
      }
    }
    const peelers = 2 + Math.min(3, Math.floor(grow / 2));
    for (let i = 0; i < peelers; i++) {
      const across = (i - (peelers - 1) / 2) * spacing * 1.5;
      this.acquireMember(
        DARTLING_ID,
        {
          x: origin.x + axis.x * (rows * spacing) + perp.x * across,
          y: origin.y + axis.y * (rows * spacing) + perp.y * across
        },
        tick,
        telegraphUntil,
        i + 1 // peel order
      );
    }
  }

  /** Pincer: two weaver wings mirrored across the approach axis. */
  private spawnPincer(
    player: Vec2,
    axis: Vec2,
    tick: number,
    telegraphUntil: number,
    grow: number
  ): void {
    const wingSize = 3 + Math.min(4, grow);
    const spread = 1.05; // wing center angle off the head-on axis (radians)
    for (let side = 0; side < 2; side++) {
      const sign = side === 0 ? 1 : -1;
      const centerAngle = Math.atan2(axis.y, axis.x) + spread * sign;
      const center = {
        x: player.x + Math.cos(centerAngle) * 26,
        y: player.y + Math.sin(centerAngle) * 26
      };
      for (let i = 0; i < wingSize; i++) {
        const off = (i - (wingSize - 1) / 2) * 2.2;
        // Fan each wing along its own tangent so it reads as a claw, not a blob.
        this.acquireMember(
          WEAVER_ID,
          {
            x: center.x + -Math.sin(centerAngle) * off,
            y: center.y + Math.cos(centerAngle) * off
          },
          tick,
          telegraphUntil,
          side
        );
      }
    }
  }

  /** Spearhead: bulwark tip with a void-fiend file behind, on the axis. */
  private spawnSpearhead(
    player: Vec2,
    axis: Vec2,
    tick: number,
    telegraphUntil: number,
    grow: number
  ): void {
    const file = 4 + Math.min(5, grow);
    const spacing = 1.9;
    const tip = { x: player.x + axis.x * 32, y: player.y + axis.y * 32 };
    this.acquireMember(BULWARK_ID, { x: tip.x, y: tip.y }, tick, telegraphUntil, 0);
    for (let i = 0; i < file; i++) {
      const back = (i + 1) * spacing;
      this.acquireMember(
        VOID_FIEND_ID,
        { x: tip.x + axis.x * back, y: tip.y + axis.y * back },
        tick,
        telegraphUntil,
        0
      );
    }
  }

  /** Wedge: apex foremost, pairs trailing at ±24° — the classic attack V. */
  private spawnWedge(
    player: Vec2,
    axis: Vec2,
    tick: number,
    telegraphUntil: number,
    grow: number
  ): void {
    const rows = 2 + Math.min(2, Math.floor(grow / 2)); // trailing pairs
    const apex = { x: player.x + axis.x * 28, y: player.y + axis.y * 28 };
    this.acquireMember(VOID_FIEND_ID, { x: apex.x, y: apex.y }, tick, telegraphUntil, 0);
    const back = Math.atan2(axis.y, axis.x); // direction away from the player
    for (let r = 1; r <= rows; r++) {
      for (let side = 0; side < 2; side++) {
        const sign = side === 0 ? 1 : -1;
        const a = back + sign * 0.42; // ±24° off the retreat axis
        const dist = r * 2.6;
        this.acquireMember(
          VOID_FIEND_ID,
          { x: apex.x + Math.cos(a) * dist, y: apex.y + Math.sin(a) * dist },
          tick,
          telegraphUntil,
          0
        );
      }
    }
  }
}
